import { css } from '@styled-system/css';
import { AlertTriangle, Info, OctagonAlert, X } from 'lucide-react';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useAnalysisEngineStatus,
  type AnalysisEngineSeverity,
} from '@/shared/hooks/analysis/useAnalysisEngineStatus';

// ============================================================================
// Bandeau d'état du moteur d'analyses — « analyses indisponibles » est un ÉTAT
// DIT, jamais un timeout muet (spec du filter-service, § « Contrat front »).
//
// EN FLUX, pas `fixed` : il se pose en tête du contenu de la page, là où
// l'utilisateur s'apprête à lancer un calcul. Le seul bandeau `fixed` de l'app
// est `LegacyVersionBanner` — un second flotterait par-dessus lui.
// Moteur sain ⇒ ce composant ne rend RIEN : zéro coût visuel en régime normal,
// qui est le cas quasi permanent.
// ============================================================================

/** Fermeture keyée par GRAVITÉ et bornée à l'onglet : une aggravation réapparaît. */
const DISMISS_KEY_PREFIX = 'analysisEngineBanner.dismissed:';

// Repris à l'identique du bandeau `scanTruncated` de la page d'analyse
// (`AnalysisDetailPage`) : même grille, même graisse, même rayon — c'est le
// précédent de l'app pour un avis en flux, il n'y a pas de raison d'en
// inventer un second.
const banner = css({
  position: 'relative',
  display: 'flex',
  alignItems: 'flex-start',
  gap: '3',
  width: '100%',
  paddingY: '3',
  paddingLeft: '4',
  paddingRight: '9',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderRadius: 'md',
  fontSize: 'caption',
  lineHeight: 1.55,
});

// ⚠️ Trois classes STATIQUES et non un token calculé : Panda extrait les styles
// à la compilation — `bg: tone.soft` ne produirait aucune règle CSS.
const tones: Record<AnalysisEngineSeverity, string> = {
  info: css({ bg: 'info.soft', borderColor: 'info.border', color: 'info.default' }),
  warning: css({ bg: 'warning.soft', borderColor: 'warning.border', color: 'warning.default' }),
  critical: css({ bg: 'error.soft', borderColor: 'error.border', color: 'error.default' }),
};

const icons: Record<AnalysisEngineSeverity, typeof Info> = {
  info: Info,
  warning: AlertTriangle,
  critical: OctagonAlert,
};

const iconWrap = css({
  flexShrink: 0,
  // Aligne l'icône sur la première ligne de texte, pas sur le bloc entier.
  marginTop: '0.5',
});

const body = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '1',
  minWidth: 0,
});

const detail = css({
  fontSize: 'micro',
  color: 'text.tertiary',
});

const closeBtn = css({
  position: 'absolute',
  right: '2',
  top: '2',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '24px',
  height: '24px',
  borderRadius: 'md',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  color: 'text.tertiary',
  _hover: { color: 'text.primary', bg: 'surface.sunken' },
});

export interface AnalysisEngineBannerProps {
  className?: string;
}

export const AnalysisEngineBanner: FC<AnalysisEngineBannerProps> = ({ className }) => {
  const { t } = useTranslation();
  const { status } = useAnalysisEngineStatus();
  const [dismissedSeverity, setDismissedSeverity] = useState<AnalysisEngineSeverity | null>(null);

  if (!status) return null;

  const severity = status.severity;
  const storageKey = `${DISMISS_KEY_PREFIX}${severity}`;

  // Relu à CHAQUE rendu, jamais une seule fois à l'initialisation : la gravité
  // change sous le sondage, et c'est elle qui porte la clé de fermeture.
  let dismissed = dismissedSeverity === severity;
  if (!dismissed) {
    try {
      dismissed = window.sessionStorage.getItem(storageKey) === '1';
    } catch {
      dismissed = false;
    }
  }
  if (dismissed) return null;

  const Icon = icons[severity];

  // Détail discret : les journées concernées. Le message de l'API dit déjà
  // COMBIEN et POURQUOI — on n'ajoute que les dates, utiles pour recouper.
  const details: string[] = [];
  if (status.staleDays.length > 0) {
    details.push(t('analysisEngine.staleDays', { days: status.staleDays.join(', ') }));
  }
  if (status.incompleteVectorDays.length > 0) {
    details.push(t('analysisEngine.incompleteVectorDays', { days: status.incompleteVectorDays.join(', ') }));
  }

  const dismiss = () => {
    setDismissedSeverity(severity);
    try {
      window.sessionStorage.setItem(storageKey, '1');
    } catch {
      // Stockage indisponible : fermé pour cette vue seulement.
    }
  };

  return (
    <div
      role="status"
      aria-label={t('analysisEngine.label')}
      className={`${banner} ${tones[severity]} ${className ?? ''}`}
    >
      <Icon size={13} strokeWidth={1.75} className={iconWrap} />
      <div className={body}>
        <span>
          {t(`analysisEngine.messages.${status.messageCode}`, {
            // ⚠️ Compte tiré de `incompleteVectorDays`, jamais transmis à part : deux sources pour le
            // même nombre finiraient par diverger. Seul `vectorsInactive` l'interpole, les autres
            // clés l'ignorent sans dommage.
            count: status.incompleteVectorDays.length,
          })}
        </span>
        {details.length > 0 && <span className={detail}>{details.join(' · ')}</span>}
      </div>
      <button type="button" className={closeBtn} onClick={dismiss} aria-label={t('common.close')}>
        <X size={14} strokeWidth={2} />
      </button>
    </div>
  );
};
